// /sinyaller — İki Katmanlı Timeline (CR2-05).
// Üst katman: push · accent border-left, full opacity.
// Alt katman: low-score · dashed border-left, opacity 0.55, collapsible.
// Satır tıkla → /sinyal/:fund (deep-link). "yeni" → "incelendi" tıklamayla
// (POST /signals/:id/status).
//
// Filtre çubuğu: zaman (bugün/hafta/ay) + durum (tümü/yeni/incelendi/aksiyon/atlandı).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSignalsRecent,
  setSignalStatus,
  ApiError,
  type ApiConfig,
} from "../lib/api";
import type {
  Anomaly,
  QueuedSignal,
  SignalStatus,
} from "../types/api";
import AnomalyBadge from "../components/AnomalyBadge";
import SinyalSatiri from "../components/SinyalSatiri";
import FreshnessIndicator from "../components/FreshnessIndicator";

interface Props {
  cfg: ApiConfig;
  openAnomalies: Anomaly[];
  openAnomaliesCount: number;
}

type ZamanFilter = "bugun" | "hafta" | "ay";
type DurumFilter = "tumu" | SignalStatus;

const ZAMAN_WINDOW_HOURS: Record<ZamanFilter, number> = {
  bugun: 24,
  hafta: 24 * 7,
  ay: 24 * 30,
};

const ZAMAN_OPTIONS: { value: ZamanFilter; label: string }[] = [
  { value: "bugun", label: "bugün" },
  { value: "hafta", label: "hafta" },
  { value: "ay", label: "ay" },
];

const DURUM_OPTIONS: { value: DurumFilter; label: string }[] = [
  { value: "tumu", label: "Hepsi" },
  { value: "yeni", label: "yeni" },
  { value: "incelendi", label: "incelendi" },
  { value: "aksiyon", label: "aksiyon" },
  { value: "atlandı", label: "atlandı" },
];

function pillClass(active: boolean): string {
  const base = "px-2 py-1 rounded transition";
  return active
    ? `${base} bg-accent-500 text-ink-900 font-semibold border border-accent-400`
    : `${base} bg-ink-800 text-ink-300 hover:text-ink-100 border border-ink-700`;
}

export default function Sinyaller({
  cfg,
  openAnomalies,
  openAnomaliesCount,
}: Props) {
  const navigate = useNavigate();
  const [signals, setSignals] = useState<QueuedSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, SignalStatus>
  >({});
  const [lowOpen, setLowOpen] = useState(false);
  const [zaman, setZaman] = useState<ZamanFilter>("bugun");
  const [durum, setDurum] = useState<DurumFilter>("tumu");

  // Sayfa mount anındaki referans zaman — filtreler bu noktaya göre süzer.
  const nowMs = useMemo(() => Date.now(), []);

  useEffect(() => {
    if (!cfg.base) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSignalsRecent(cfg, { days: 30, includeLowScore: true })
      .then((out) => {
        if (!cancelled) setSignals(out.items);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : "Beklenmedik hata",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cfg]);

  const { pushList, lowList } = useMemo(() => {
    const windowMs = ZAMAN_WINDOW_HOURS[zaman] * 3600 * 1000;
    const statusOf = (s: QueuedSignal): SignalStatus =>
      statusOverrides[s.id] ?? s.default_status;
    const passes = (s: QueuedSignal): boolean => {
      const ageMs = nowMs - new Date(s.triggered_at).getTime();
      if (ageMs > windowMs) return false;
      if (durum !== "tumu" && statusOf(s) !== durum) return false;
      return true;
    };
    const push: QueuedSignal[] = [];
    const low: QueuedSignal[] = [];
    for (const s of signals) {
      if (!passes(s)) continue;
      if (s.tier === "push") push.push(s);
      else low.push(s);
    }
    return { pushList: push, lowList: low };
  }, [signals, zaman, durum, statusOverrides, nowMs]);

  function statusOf(s: QueuedSignal): SignalStatus {
    return statusOverrides[s.id] ?? s.default_status;
  }

  function handleSignalClick(sig: QueuedSignal) {
    // "yeni" status'unu "incelendi"ye çevir — optimistic + POST.
    const current = statusOf(sig);
    if (current === "yeni") {
      setStatusOverrides((prev) => ({ ...prev, [sig.id]: "incelendi" }));
      void setSignalStatus(cfg, sig.id, "incelendi").catch(() => {
        // hata olursa görsel sessiz, server source of truth bir sonraki reload.
      });
    }
    navigate(`/sinyal/${sig.fund}`);
  }

  // openAnomalies App'ten geliyor; fonun açık anomalisini fonun chip'i için süzelim.
  function anomaliesForFund(fund: string): Anomaly[] {
    return openAnomalies.filter((a) => a.fund === fund);
  }

  return (
    <main
      className="min-h-screen flex flex-col mx-auto"
      style={{ maxWidth: 430 }}
    >
      {/* Header */}
      <header className="sticky top-0 z-20 bg-ink-900/95 backdrop-blur border-b border-ink-700/60">
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <div className="flex items-baseline gap-2 min-w-0">
            <button
              onClick={() => navigate("/")}
              className="text-ink-100 font-semibold tracking-tight text-base hover:text-accent-300 transition"
            >
              AiFon
            </button>
            <span className="text-ink-400 text-sm">·</span>
            <span className="text-ink-300 text-sm">Sinyaller</span>
          </div>
          <div className="flex items-center gap-1">
            <AnomalyBadge
              count={openAnomaliesCount}
              onClick={() => navigate("/anomaliler")}
            />
          </div>
        </div>
      </header>

      <section className="px-4 pt-5">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate("/")}
            aria-label="Geri"
            className="text-ink-300 hover:text-ink-100 -ml-1 p-1"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="grow min-w-0">
            <h2 className="hedef-dili text-ink-50 text-xl leading-snug">
              Sinyaller
            </h2>
            <p className="text-ink-300 text-sm mt-0.5">
              {loading
                ? "yükleniyor…"
                : `${pushList.length} push · ${lowList.length} low-score · iki katmanlı`}
            </p>
          </div>
        </div>
      </section>

      {error && (
        <section className="px-4 mt-3">
          <div className="rounded-lg bg-loss-500/10 border border-loss-500/30 px-3 py-2 text-[12px] text-loss-400">
            Sinyal listesi yüklenemedi: {error}
            {!cfg.base && (
              <span className="block mt-1 text-ink-300">
                Settings'ten API base'i ayarla.
              </span>
            )}
          </div>
        </section>
      )}

      <div className="px-4 mt-3">
        <FreshnessIndicator
          lastPriceDate="2026-05-26"
          indexPeriod="2026-04"
          signalRefreshedAt="2026-05-26T19:00:00+03:00"
          mock
          compact
        />
      </div>

      {/* Filtre çubuğu — local state, aktif pill accent-bg */}
      <section className="px-4 mt-4" aria-label="Sinyal filtreleri">
        <div className="flex flex-wrap gap-1.5 text-[11px]">
          <span className="text-ink-400 mr-1 self-center">Zaman:</span>
          {ZAMAN_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setZaman(opt.value)}
              aria-pressed={zaman === opt.value}
              className={pillClass(zaman === opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-1.5 text-[11px]">
          <span className="text-ink-400 mr-1 self-center">Durum:</span>
          {DURUM_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDurum(opt.value)}
              aria-pressed={durum === opt.value}
              className={pillClass(durum === opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      {/* Push'lanan */}
      <section className="px-4 mt-5">
        <h3 className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-2 flex items-center gap-2">
          <span
            className="w-1.5 h-1.5 rounded-full bg-accent-400"
            aria-hidden="true"
          />
          Push&apos;lanan · {pushList.length}
        </h3>
        {loading ? (
          <p className="text-[11px] text-ink-400 italic px-1 py-2">Yükleniyor…</p>
        ) : pushList.length === 0 ? (
          <p className="text-[11px] text-ink-400 italic px-1 py-2">
            Bu filtreye uyan push sinyal yok.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {pushList.map((sig) => (
              <li key={sig.id}>
                <SinyalSatiri
                  signal={sig}
                  status={statusOf(sig)}
                  anomaliesForFund={anomaliesForFund(sig.fund)}
                  onClick={handleSignalClick}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Low-score (collapsible kapalı default) */}
      <section className="px-4 mt-5">
        <button
          type="button"
          onClick={() => setLowOpen((v) => !v)}
          className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-400 font-semibold hover:text-ink-200 w-full"
          aria-expanded={lowOpen}
        >
          <span
            className="caret inline-block transition-transform"
            style={{ transform: lowOpen ? "rotate(90deg)" : "rotate(0deg)" }}
          >
            ▸
          </span>
          <span
            className="w-1.5 h-1.5 rounded-full bg-ink-500"
            aria-hidden="true"
          />
          Low-score · {lowList.length}
          <span className="text-ink-500 normal-case font-normal ml-1">
            (skor &lt; 50)
          </span>
        </button>

        {lowOpen &&
          (loading ? (
            <p className="text-[11px] text-ink-500 italic px-1 py-2 mt-3">
              Yükleniyor…
            </p>
          ) : lowList.length === 0 ? (
            <p className="text-[11px] text-ink-500 italic px-1 py-2 mt-3">
              Bu filtreye uyan low-score sinyal yok.
            </p>
          ) : (
            <ul className="flex flex-col gap-2 mt-3">
              {lowList.map((sig) => (
                <li key={sig.id}>
                  <SinyalSatiri
                    signal={sig}
                    status={statusOf(sig)}
                    anomaliesForFund={anomaliesForFund(sig.fund)}
                    onClick={handleSignalClick}
                  />
                </li>
              ))}
            </ul>
          ))}
      </section>

      <footer className="mt-auto px-4 pb-6 pt-8 text-center text-[11px] text-ink-400">
        AiFon · İki katmanlı timeline
      </footer>
    </main>
  );
}
